import { Config } from "@config";
import { calculate } from "@component/BabyLoan/SmallCalculator/domain";
import type { BabyLoanRequest, BabyLoanResponse } from "@component/BabyLoan/SmallCalculator/types";
import { http } from "bm-ui-kit";
import type { LoanGoal } from "./Steps/Step2.data";
import type { CsokResponse, CsokResult, FamilyAndHousingAllowanceFormFields } from "./types";

export function isBuildingGoal(loanGoal: LoanGoal): boolean {
	return ["EPITES", "TETOTER_BEEPITES"].includes(loanGoal);
}

export function calculateCsok(values: FamilyAndHousingAllowanceFormFields): Promise<CsokResult> {
	const {
		loanGoal,
		married,
		numberOfChildren,
		numberOfChildrenCommitment,
		taxDept,
		socialInsuranceSinceId,
		realEstateValue,
		realEstateSizeId,
		ownRealEstateEnlargement,
		buyFromCloseRelative,
		earlierBoughtRealEstate,
		generalContractor,
		plannedBudget,
		hasMoreThenHalfOwnership,
		homeMeetsTheOneYearRequirement,
	} = values;

	const data = {
		married,
		numberOfChildren,
		numberOfChildrenCommitment,
		taxDept,
		realEstateValue,
		realEstateSizeId,
		ownRealEstateEnlargement,
		buyFromCloseRelative,
		earlierBoughtRealEstate,
		generalContractor,
		loanGoal: loanGoal === "TETOTER_BEEPITES" ? "EPITES" : loanGoal,
		socialInsuranceSinceId: getCsokSocialInsuranceSinceId(socialInsuranceSinceId),
	};

	let vatRefund: number;
	let feeDiscount: number;
	let renovationAid: number;

	const url = Config.getApiUrl("/api/public/csok/calculate", data);

	const renovationErrorMessages = [];

	if (["FELUJITAS", "KORSZERUSITES"].includes(loanGoal)) {
		if (numberOfChildren === 0) {
			renovationErrorMessages.push("Csak gyermeket nevelő szülő igényelheti a támogatást.");
		}

		if (socialInsuranceSinceId < 2) {
			renovationErrorMessages.push("Legalább 1 éves Tb jogviszonyt kell igazolnod.");
		}

		if (taxDept) {
			renovationErrorMessages.push("Nem lehet adótartozásod.");
		}

		if (!hasMoreThenHalfOwnership) {
			renovationErrorMessages.push(
				"A felújítandó ingatlanban a tulajdoni hányadotok – a te, a házas-, élettársad és gyermeked tulajdoni hányada együttesen – nem éri el az 50%-ot.",
			);
		}

		if (!homeMeetsTheOneYearRequirement) {
			renovationErrorMessages.push("Legalább 1 éve a felújítandó lakásban szükséges laknotok.");
		}

		if (!renovationErrorMessages.length) {
			renovationAid = Math.min(plannedBudget / 2, 3_000_000);
		}
	} else {
		renovationErrorMessages.push("Kizárólag felújításra, korszerűsítésre igényelhető a támogatás");
	}

	if (isBuildingGoal(loanGoal) && !generalContractor && !taxDept && socialInsuranceSinceId > 0) {
		vatRefund = Math.min(realEstateValue - realEstateValue / 1.27, 5_000_000);
	}

	return http.get(url).then((response: CsokResponse) => {
		if (response.canGetCsok) {
			switch (loanGoal) {
				case "UJ_LAKAS":
					vatRefund = realEstateValue - realEstateValue / 1.05;
					feeDiscount = realEstateValue * 0.04;
					break;
				case "EPITES":
				case "TETOTER_BEEPITES":
					if (generalContractor) {
						vatRefund = realEstateValue - realEstateValue / 1.05;
					}
					break;
				case "HASZNALT_LAKAS":
					feeDiscount = realEstateValue * 0.04;
					break;
				default:
			}
		}

		return {
			...response,
			renovationErrorMessages,
			vatRefund,
			feeDiscount,
			renovationAid,
		};
	});
}

export function calculateBabyLoan(values: FamilyAndHousingAllowanceFormFields): BabyLoanResponse {
	const {
		creditLimitSumValue,
		currentLoansMonthlyInstallment,
		loanAmount,
		birthDate,
		inKHR,
		married,
		hasBothOfTheCoupleHunAddress,
		taxDept,
		isLast180daysSocInHun,
		incomeList,
	} = values;

	const data: BabyLoanRequest = {
		extendBabyLoanWith: values.otherRequiredProducts.includes("MORTGAGE") ? ["CSOK", "MORTGAGE"] : ["CSOK"],
		creditLimitSumValue,
		currentLoansMonthlyInstallment,
		loanAmount,
		birthDate,
		inKHR,
		married,
		hasBothOfTheCoupleHunAddress,
		hasEitherOfCoupleNavDebt: taxDept,
		isLast180daysSocInHun,
		hasEitherOfCoupleSocialInsurance: values.socialInsuranceSinceId < 3 ? "LESS_THAN_THREE" : "THREE_AND_MORE",
		incomeList,
		wantsWithOnlineService: false,

		productName: "",
	};

	return calculate(data);
}

export function calculateMortgage(
	values: FamilyAndHousingAllowanceFormFields,
	babyLoanAmount: number,
	correctedNetIncome: number,
): Promise<number> {
	const { currentLoansMonthlyInstallment, creditLimitSumValue, otherRequiredProducts } = values;

	if (!otherRequiredProducts.includes("MORTGAGE") || values.inKHR) {
		return Promise.resolve(0);
	}

	return http
		.post(Config.getApiUrl("/api/public/mortgages/max-loan-amount"), {
			correctedNetIncome,
			currentLoansMonthlyInstallment,
			creditLimitSumValue,
			babyLoanAmount,
		})
		.then((response: number) => Math.floor(Number(response) / 50_000) * 50_000);
}

export function getCsokSocialInsuranceSinceId(socialInsuranceSinceId: number): number {
	if (socialInsuranceSinceId < 1) {
		return 1;
	}

	if (socialInsuranceSinceId <= 2) {
		return 2;
	}

	return 3;
}

export function getTotalAmount(csokResult: CsokResult): number {
	return (
		(csokResult?.csokAmount || 0) +
		(csokResult?.renovationAid || 0) +
		(csokResult?.feeDiscount || 0) +
		(csokResult?.vatRefund || 0)
	);
}
